import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Building2, Scissors, GraduationCap } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";

const hero = "https://images.unsplash.com/photo-1634573615282-84b2338aeea3?crop=entropy&cs=srgb&fm=jpg&q=85";
const estate = "https://customer-assets-lqy194kg.emergentagent.net/job_museo-urbino-hub/artifacts/9zfpoarq_vista_aerea_con_piscina_e_ristorante.jpg";
const artisan = "https://customer-assets-lqy194kg.emergentagent.net/job_museo-urbino-hub/artifacts/e8c1yzgy_IMG_4113.webp";
const students = "https://customer-assets-lqy194kg.emergentagent.net/job_museo-urbino-hub/artifacts/1he755eq_WhatsApp%20Image%202026-08-27%20at%2016.38.32.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" } }),
};

export default function Home() {
  return (
    <Layout>
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden paper-grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0} className="overline">Urbino · Marche · Italia</motion.div>
            <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-4 text-[#1A1A1A]">
              Nessuna definizione<br/>la racchiude.<br/><em className="text-[#8C3A27] not-italic">Bisogna viverla.</em>
            </motion.h1>
            <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2}
              className="mt-6 text-lg text-[#6B6960] max-w-xl leading-relaxed">
              La Corte della Miniera è agriturismo, centro artistico e casa di Museum Graphia,
              museo internazionale della stampa. Un luogo dove scuole, gruppi e famiglie si
              immergono in laboratori d'arte e artigianato, tra le colline di Urbino.
            </motion.p>
            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={3} className="mt-8 flex flex-wrap gap-3">
              <Link to="/prenota">
                <Button data-testid="hero-book-btn" className="btn-primary-cdm rounded-full px-7 h-12 text-base">
                  Prenota il tuo gruppo <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/museum-graphia">
                <Button variant="outline" className="rounded-full h-12 px-7 border-[#1A1A1A]/20 text-[#1A1A1A]">
                  Scopri il Museo
                </Button>
              </Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}
            className="lg:col-span-6 relative">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img src={hero} alt="Museum Graphia · torchio da stampa" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#F9F8F6] border border-[#E2DFD6] px-6 py-4 hidden md:block">
              <div className="overline">Museum Graphia</div>
              <div className="font-display text-2xl text-[#1A1A1A]">Museo internazionale della stampa</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 border-t border-[#E2DFD6]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { icon: BookOpen, title: "Museum Graphia", text: "Esperienza museale personale, polivalente, multifunzionale. Spazio, oggetto e attività si fondono in un contesto di apprendimento unico." },
            { icon: Building2, title: "Agriturismo & Centro Artistico", text: "Ospitalità per scuole e gruppi in agriturismo, con centro artistico attrezzato: moduli di attività da 3 ore per minimo 15 partecipanti." },
            { icon: Scissors, title: "Laboratori per tutte le età", text: "La Stampa in tutte le sue definizioni. Corsi easy e professionali dedicati alle persone più esigenti." },
          ].map((p, i) => (
            <motion.div key={p.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <p.icon size={28} className="text-[#8C3A27]" />
              <h3 className="font-display text-3xl mt-4 text-[#1A1A1A]">{p.title}</h3>
              <p className="mt-3 text-[#6B6960] leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STORY BAND */}
      <section className="bg-[#2A312B] text-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="overline text-[#D7B565]">La nostra storia</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4">Una miniera di Zolfo,<br/>diventata bottega d'arte.</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-[#F9F8F6]/80 leading-relaxed">
            Il "laboratorio", quale contesto di apprendimento, è un ambiente esclusivo per imparare
            a ragionare, acquisire capacità critiche, costruire conoscenze e raggiungere abilità
            trasversali a quelle scolastiche. La Corte della Miniera vive tutto questo tra torchi
            e pietre litografiche, nel cuore delle colline urbinati.
          </div>
        </div>
      </section>

      {/* BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Servizi & Progetti</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-3">Cosa puoi vivere qui</h2>
          </div>
          <Link to="/servizi" className="hidden md:inline-flex text-sm link-underline">Tutti i laboratori →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <BentoCard className="md:col-span-4 md:row-span-2 aspect-[16/9] md:aspect-auto" image={estate} title="Corte della Miniera" subtitle="Ospitalità" to="/corte-della-miniera" />
          <BentoCard className="md:col-span-2" image={artisan} title="Laboratori artigiani" subtitle="Servizi" to="/servizi" />
          <BentoCard className="md:col-span-2" image={students} title="PCTO · Erasmus VET" subtitle="Formazione" to="/progetti" />
        </div>
      </section>

      {/* PROJECTS STRIPE */}
      <section className="border-t border-[#E2DFD6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="overline">Formazione</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-3">Scuola, Erasmus, Adulti.</h2>
            <p className="mt-5 text-[#6B6960] leading-relaxed max-w-lg">
              Progetti strutturati di Formazione Scuola Lavoro (PCTO), mobilità Erasmus+ VET
              e corsi intensivi per adulti. Ogni percorso è co-progettato con i docenti referenti.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/progetti"><Button variant="outline" className="rounded-full h-11 px-6 border-[#1A1A1A]/20">Vedi i progetti</Button></Link>
              <Link to="/prenota"><Button className="btn-primary-cdm rounded-full h-11 px-6">Richiedi preventivo</Button></Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: GraduationCap, title: "PCTO", note: "Percorsi Scuola Lavoro" },
              { icon: BookOpen, title: "Erasmus+", note: "Mobilità VET" },
              { icon: Scissors, title: "Sartoriale", note: "Progetti speciali" },
              { icon: Building2, title: "Adulti", note: "Corsi intensivi" },
            ].map((c) => (
              <div key={c.title} className="border border-[#E2DFD6] bg-white p-6 hover-lift">
                <c.icon size={22} className="text-[#8C3A27]" />
                <div className="font-display text-2xl mt-4">{c.title}</div>
                <div className="text-sm text-[#6B6960] mt-1">{c.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="bg-[#8C3A27] text-[#F9F8F6] px-10 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="overline text-[#D7B565]">Prenotazione gruppi</div>
            <h3 className="font-display text-4xl mt-3 max-w-xl">Componi il tuo soggiorno, scegli i laboratori.</h3>
          </div>
          <Link to="/prenota">
            <Button data-testid="cta-book-btn" className="bg-[#F9F8F6] text-[#1A1A1A] hover:bg-white rounded-full h-12 px-7">
              Inizia la prenotazione <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}

function BentoCard({ className = "", image, title, subtitle, to }) {
  return (
    <Link to={to} className={`group relative overflow-hidden block ${className}`}>
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-end p-6 min-h-[220px]">
        <div className="overline text-[#D7B565]">{subtitle}</div>
        <div className="font-display text-3xl text-white mt-1">{title}</div>
      </div>
    </Link>
  );
}
